import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, Loader2, Paperclip, Search, ExternalLink, Mic, BellRing, CalendarPlus, Mail, Handshake, Copy, AlignLeft, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import RobotFace from "@/components/RobotFace";
import LocationButton from "@/components/LocationButton";
import SourceBadge from "@/components/SourceBadge";
import { WeatherCard } from "@/components/chat/WeatherCard";
import { useShowSources, useShowConfidence } from "@/hooks/useChatPrefs";
import { readConsent as readLocationConsent } from "@/hooks/useExactLocation";
import { isUncertainReply } from "@/lib/uncertainty";
import VoiceMode from "@/components/VoiceMode";
import HeroRing from "@/components/landing/HeroRing";

/**
 * Read fresh exact-location headers from localStorage at request time
 * so the SSE fetch always sees the latest share/clear action from the
 * LocationButton. Returns an empty object when consent is off or the
 * stored fix is missing / expired.
 */
function _readExactLocationHeaders() {
  try {
    if (!readLocationConsent()) return {};
    const raw = localStorage.getItem("rammy_exact_location");
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (!data || typeof data.lat !== "number" || typeof data.lng !== "number") return {};
    if (!data.ts || Date.now() - data.ts > 15 * 60 * 1000) return {};
    return {
      "X-User-Lat": String(data.lat),
      "X-User-Lng": String(data.lng),
      "X-User-Accuracy": String(Math.round(data.accuracy || 0)),
    };
  } catch { return {}; }
}

// Suggestion chips shown under the hero input. Grouped by shape:
//   - Companion-magic: prompts that hint at what makes Rammy different (memory,
//     connectors, calendar writes, promises). These get the accent styling.
//     Every magic chip MUST map to a capability that actually ships — a chip
//     that leads to a shrug is the worst possible first impression.
//   - Everyday: the "just chat with an AI" surface most users land expecting.
// We show 3 of each so the row is asymmetric and doesn't read as a boring menu.
const SUGGESTIONS = [
  { label: "Plan my week from my calendar",     text: "Plan my week. Your calendar isn't connected yet, so ask me for the handful of things that are already fixed in my week, then build the plan around them.", magic: true },
  { label: "Turn this promise into a task",     text: "In my last email I said \"I'll send this tomorrow\" — capture that as a task and remind me before 6pm tomorrow.", magic: true },
  { label: "Book 30 minutes tomorrow",          text: "Book me 30 minutes tomorrow morning to write the investor update, and keep it away from my other meetings.", magic: true },
  { label: "Talk to Rammy",                      text: "Hey Rammy, how are you today?" },
  { label: "Cheer me up",                       text: "I'm feeling a bit flat today. Say something kind and get me moving." },
  { label: "Remember this",                     text: "Remember: I love oat milk lattes, my sister's birthday is March 3, and I run every Sunday." },
];

function getAnonSessionId() {
  try {
    let sid = localStorage.getItem("rammy_anon_session");
    if (!sid) {
      sid = (crypto.randomUUID && crypto.randomUUID()) || `anon-${Math.random().toString(36).slice(2)}-${Date.now()}`;
      localStorage.setItem("rammy_anon_session", sid);
    }
    return sid;
  } catch {
    return `anon-${Math.random().toString(36).slice(2)}-${Date.now()}`;
  }
}

/**
 * Strip / normalise light markdown that some LLMs still emit.
 * Keeps output plain-text conversational — matches Rammy's warm voice.
 */
function cleanReply(s) {
  if (!s) return s;
  return s
    // Regeneration hints ride along invisibly on the user's own message.
    .replace(/<rammy:style>[\s\S]*?<\/rammy:style>/gi, "")
    // Internal directives are stripped server-side now; this is the belt to
    // that braces, so a raw `<generate .../>` can never reach a visitor.
    .replace(/<generate\s+(?:[^"/>]|"[^"]*")*?\s*\/>/gi, "")
    .replace(/<generate\b[^>]*$/i, "")
    // bold **text** or __text__
    .replace(/\*\*([\s\S]+?)\*\*/g, "$1")
    .replace(/__([\s\S]+?)__/g, "$1")
    // italic *text* or _text_ (avoid touching inner punctuation like a*b)
    .replace(/(^|[\s(])\*(?!\s)([^*\n]+?)\*(?=[\s.,!?;:)]|$)/g, "$1$2")
    .replace(/(^|[\s(])_(?!\s)([^_\n]+?)_(?=[\s.,!?;:)]|$)/g, "$1$2")
    // inline code `text`
    .replace(/`([^`\n]+)`/g, "$1")
    // heading marks
    .replace(/^#{1,6}\s+/gm, "")
    // list bullets "- " / "* " / "+ " → "• "
    .replace(/^\s*[-*+]\s+/gm, "• ")
    // numbered list "1. " → keep number but tidy
    .replace(/^\s*(\d+)\.\s+/gm, "$1. ")
    // triple+ blank lines → double
    .replace(/\n{3,}/g, "\n\n");
}


/** Something Rammy captured for a visitor it has no way to reach yet.
 *  A reminder or a promise switches on the moment they save the chat; a
 *  calendar event or an email needs Google connected first, so we say that
 *  instead of pretending. */
function PendingActionCard({ action, testid }) {
  const { kind, title, when, activatable } = action;
  const Icon = kind === "calendar-event" ? CalendarPlus
    : kind === "gmail-draft" ? Mail
    : kind === "commitment" ? Handshake
    : BellRing;
  const label = kind === "calendar-event" ? "Draft event"
    : kind === "gmail-draft" ? "Draft email"
    : kind === "commitment" ? "Draft promise"
    : "Draft reminder";
  // Honest before the verb: nothing here is armed. The second QA audit rated
  // "REMINDER READY" for a visitor as a critical trust defect — the user
  // walks away believing they're covered when nothing will ever fire.
  const line = activatable
    ? "It can't reach you yet — this starts working the moment you save this chat."
    : kind === "gmail-draft"
      ? "It isn't in your inbox yet — Rammy can put it there once you're signed in with Gmail connected."
      : "It isn't on your calendar yet — Rammy can add it once you're signed in with your calendar connected.";
  return (
    <div
      className="rounded-2xl border border-dashed border-[var(--rammy-cyan)]/50 bg-[var(--rammy-cyan)]/[0.07] p-3.5 flex items-start gap-3"
      data-testid={testid}
    >
      <span className="mt-0.5 text-[var(--rammy-cyan-dark)] shrink-0"><Icon size={15} strokeWidth={1.9}/></span>
      <div className="min-w-0 flex-1 text-left">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-cyan-dark)]">{label}</div>
        <div className="text-[15px] font-medium leading-snug mt-0.5" data-testid={`${testid}-title`}>{title}</div>
        {when && (
          <div className="text-[12px] text-[var(--rammy-text-sec)] mt-0.5" data-testid={`${testid}-when`}>
            {formatWhen(when)}
          </div>
        )}
        <div className="text-[12px] text-[var(--rammy-text-sec)] mt-1.5 leading-relaxed">{line}</div>
        {activatable && (
          <Link to="/signup" data-testid={`${testid}-save`} className="inline-block mt-2.5">
            <Button className="btn-pill btn-primary h-8 px-3.5 text-[11px]">Save this chat — free</Button>
          </Link>
        )}
      </div>
    </div>
  );
}

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    // Both clocks, always. The audit watched a card read "Thursday, 9:00 AM"
    // to a user who had spoken entirely in IST — the arithmetic was right and
    // the label was missing, which is the same thing as being wrong.
    const zone = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(d).find(p => p.type === "timeZoneName")?.value || "";
    const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    const utc = d.toLocaleTimeString("en-GB", {
      hour: "2-digit", minute: "2-digit", timeZone: "UTC",
    });
    const today = new Date();
    let day;
    if (d.toDateString() === today.toDateString()) day = "Today";
    else if (new Date(today.getTime() + 86400000).toDateString() === d.toDateString()) day = "Tomorrow";
    else day = d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
    return `${day}, ${time}${zone ? ` ${zone}` : ""} · ${utc} UTC`;
  } catch (_) {
    return iso;
  }
}

export default function HeroChat() {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [expression, setExpression] = useState("idle");
  const [remaining, setRemaining] = useState(null);
  const [locked, setLocked] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [showSources] = useShowSources();
  const [showConfidence] = useShowConfidence();
  const voiceStreamAbortRef = useRef(null);   // aborts the in-flight turn (typed path)
  const voiceModeRef = useRef(false);
  const [voiceFocusMode, setVoiceFocusMode] = useState(null);
  // Companion (default) · Brief · Deep. The audit's central point: Rammy
  // answered every question like a chat reply, so it lost every "who informs
  // better" comparison even when it knew more.
  const [answerMode, setAnswerMode] = useState("companion");
  const dragDepth = useRef(0);
  const threadRef = useRef(null);
  const inputRef = useRef(null);
  const composerRef = useRef(null);
  const fileRef = useRef(null);
  const started = msgs.length > 0;

  // Free-reply count, read BEFORE the first send. The audit found visitors
  // hitting a hard wall they'd never been warned about, on a page that
  // promised no message cap at all.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/anon/quota`, {
          headers: { "X-Anon-Session": getAnonSessionId() },
        });
        if (!res.ok) return;
        const q = await res.json();
        if (!alive || typeof q?.remaining !== "number") return;
        setRemaining(q.remaining);
        if (q.remaining <= 0) setLocked(true);
      } catch (_) { /* a missing count is not worth an error state */ }
    })();
    return () => { alive = false; };
  }, []);

  // Auto-scroll the thread container itself (not the whole page).
  // This is what fixes the "page jumps" issue on mobile.
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [msgs, busy]);

  // On first send, keep the composer stable in view rather than letting the
  // whole hero shrink and yank the user upward.
  const firstSendAnchoredRef = useRef(false);
  useEffect(() => {
    if (started && !firstSendAnchoredRef.current) {
      firstSendAnchoredRef.current = true;
      // Center composer in viewport after the layout settles.
      requestAnimationFrame(() => {
        composerRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    }
  }, [started]);

  const ACCEPT = "image/jpeg,image/png,image/webp";
  const MAX_FILES = 3;
  const MAX_BYTES = 5 * 1024 * 1024;

  const onPick = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = MAX_FILES - attachments.length;
    if (room <= 0) return toast.error(`Up to ${MAX_FILES} images per message.`);
    const next = [];
    for (const f of files.slice(0, room)) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
        toast.error(`${f.name || "clipboard image"}: only JPEG, PNG, WEBP.`); continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name || "clipboard image"}: max 5 MB.`); continue;
      }
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onerror = rej;
        r.onload = () => res(String(r.result).split(",")[1]);
        r.readAsDataURL(f);
      });
      next.push({ name: f.name || `pasted-${Date.now()}.png`, data: b64, mime: f.type, preview: URL.createObjectURL(f) });
    }
    setAttachments(a => [...a, ...next]);
    if (next.length) toast.success(`${next.length} image${next.length === 1 ? "" : "s"} attached`);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) files.push(it.getAsFile());
    }
    if (files.length) {
      e.preventDefault();
      onPick(files);
    }
  };

  const hasDragFiles = (e) => Array.from(e.dataTransfer?.items || []).some(it => it.kind === "file");
  const onDragEnter = (e) => {
    if (!hasDragFiles(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragOver(true);
  };
  const onDragOver = (e) => {
    if (!hasDragFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDragLeave = (e) => {
    if (!hasDragFiles(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragOver(false);
  };
  const onDrop = (e) => {
    if (!hasDragFiles(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) onPick(files);
  };
  const removeAttachment = (i) => setAttachments(a => {
    const gone = a[i];
    if (gone?.preview) URL.revokeObjectURL(gone.preview);
    return a.filter((_, j) => j !== i);
  });

  const send = async (overrideText, opts = {}) => {
    const { verify = false } = opts;
    const text = (overrideText ?? input).trim();
    if ((!text && attachments.length === 0) || busy || locked) return;
    if (!overrideText && !verify) setInput("");
    const currentAttachments = verify ? [] : attachments;
    if (!verify) setAttachments([]);
    // On a verify tap we skip re-adding the user bubble — the original question
    // is right above the "Let me check" chip. Backend still gets the question
    // via `message` + `verify:true` so the deep web lookup can run.
    setMsgs(m => [
      ...m,
      ...(verify ? [] : [{ role: "user", content: text, images: currentAttachments.map(a => a.preview) }]),
      { role: "assistant", content: "", streaming: true, verified: verify },
    ]);
    setBusy(true); setExpression("thinking");
    // The install banner waits for this — an install modal parked over the
    // send button during someone's first evaluation is pure friction.
    try {
      sessionStorage.setItem("rammy_engaged", "1");
      window.dispatchEvent(new Event("rammy:engaged"));
    } catch (_) { /* private mode */ }
    let deltaStarted = false;
    const inVoice = voiceModeRef.current;   // captured — don't re-check via mutable ref during stream
    try {
      const sid = getAnonSessionId();
      // One controller per turn so voice mode can abandon a reply the moment
      // the user carries on talking.
      try { voiceStreamAbortRef.current?.abort(); } catch (_) { /* ignore */ }
      const controller = new AbortController();
      voiceStreamAbortRef.current = controller;
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/anon/stream`, {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          "X-Anon-Session": sid,
          // Auto-inject browser timezone so anon users get the same
          // location-aware disambiguation as signed-in users.
          "X-User-Timezone": (() => {
            try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; }
            catch (_) { return ""; }
          })(),
          // Exact GPS coordinates when the visitor tapped the "share
          // exact location" pin. Only sent while consent is on.
          ..._readExactLocationHeaders(),
        },
        body: JSON.stringify({
          message: text,
          attachments: currentAttachments.map(a => ({ data: a.data, mime: a.mime })),
          verify,
          voice: inVoice,
          answer_mode: answerMode,
        }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const evt of events) {
          const lines = evt.split("\n");
          let name = "message", data = "";
          for (const l of lines) {
            if (l.startsWith("event: ")) name = l.slice(7).trim();
            else if (l.startsWith("data: ")) data += l.slice(6);
          }
          if (!data) continue;
          let payload; try { payload = JSON.parse(data); } catch { continue; }
          if (name === "locked") {
            setLocked(true);
            setMsgs(m => {
              const copy = [...m]; const last = copy[copy.length - 1];
              if (last?.streaming) copy.pop();
              return copy;
            });
            continue;
          }
          if (name === "meta" && typeof payload.remaining === "number") setRemaining(payload.remaining);
          if (name === "meta" && payload.credibility) {
            setMsgs(m => {
              const copy = [...m]; const last = copy[copy.length - 1];
              if (last?.streaming) copy[copy.length - 1] = { ...last, credibility: payload.credibility };
              return copy;
            });
          }
          if (name === "sources" && Array.isArray(payload.sources)) {
            setMsgs(m => {
              const copy = [...m]; const last = copy[copy.length - 1];
              if (last?.streaming) copy[copy.length - 1] = { ...last, sources: payload.sources };
              return copy;
            });
          }
          if (name === "weather" && payload?.city) {
            setMsgs(m => {
              const copy = [...m];
              for (let k = copy.length - 1; k >= 0; k--) {
                if (copy[k].role === "assistant") {
                  copy[k] = { ...copy[k], weather: payload };
                  break;
                }
              }
              return copy;
            });
          }
          if (name === "pending_action" && payload?.title) {
            // Rammy captured something it can't deliver to a visitor. Show the
            // thing, not the plumbing — and say plainly what unlocks it.
            setMsgs(m => {
              const copy = [...m];
              for (let k = copy.length - 1; k >= 0; k--) {
                if (copy[k].role === "assistant") {
                  const list = copy[k].pendingActions || [];
                  copy[k] = { ...copy[k], pendingActions: [...list, payload] };
                  break;
                }
              }
              return copy;
            });
          }
          if (name === "delta" && payload.content) {
            if (!deltaStarted) { deltaStarted = true; setExpression("speaking"); }
            setMsgs(m => {
              const copy = [...m]; const last = copy[copy.length - 1];
              if (last?.streaming) copy[copy.length - 1] = { ...last, content: (last.content || "") + payload.content };
              return copy;
            });
          }
          if (name === "done") {
            const finalText = cleanReply(payload.full || "");
            setMsgs(m => {
              const copy = [...m]; const last = copy[copy.length - 1];
              if (last?.streaming) copy[copy.length - 1] = { ...last, role: "assistant", content: finalText || last.content, streaming: false };
              return copy;
            });
          }
        }
      }
      // Final safety pass — in case the last delta wasn't cleaned.
      setMsgs(m => {
        const copy = [...m]; const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.streaming) {
          copy[copy.length - 1] = { ...last, content: cleanReply(last.content) };
        }
        return copy;
      });
      setExpression("happy");
      setTimeout(() => setExpression("idle"), 2500);
    } catch (err) {
      const aborted = err?.name === "AbortError";
      setExpression(aborted ? "idle" : "sad");
      setMsgs(m => {
        const copy = [...m]; const last = copy[copy.length - 1];
        if (last?.streaming) {
          if (aborted) copy.pop();
          else copy[copy.length - 1] = { role: "assistant", content: "I'm having a quiet moment — please try again in a bit." };
        }
        return copy;
      });
    } finally { setBusy(false); }
  };

  /** Re-ask the last question, optionally reshaped. The hidden
   *  <rammy:style> marker is the same one the signed-in chat uses. */
  const regenerate = (styleKey) => {
    if (busy || locked) return;
    let userIdx = -1;
    for (let k = msgs.length - 1; k >= 0; k--) {
      if (msgs[k].role === "user") { userIdx = k; break; }
    }
    if (userIdx < 0) return;
    const base = (msgs[userIdx].content || "")
      .replace(/<rammy:style>[\s\S]*?<\/rammy:style>/gi, "").trim();
    if (!base) return;
    const text = styleKey ? `${base}\n\n<rammy:style>${styleKey}</rammy:style>` : base;
    setMsgs(m => m.slice(0, userIdx));
    setTimeout(() => send(text), 0);
  };

  return (
    <section
      className={started
        ? "fixed inset-0 z-40 flex flex-col bg-[var(--rm-bg)]"
        : "relative pt-24 md:pt-28 pb-16 md:pb-24 overflow-hidden"}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-testid="hero-dropzone"
    >
      {/* Drag overlay */}
      {dragOver && (
        <div
          className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center"
          data-testid="hero-drop-overlay"
        >
          <div className="absolute inset-6 rounded-3xl border-2 border-dashed border-[var(--rammy-cyan)] bg-[var(--rm-surface)]/85 backdrop-blur-md" />
          <div className="relative text-center px-8">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-[var(--rammy-cyan)]/25 flex items-center justify-center rammy-shadow">
              <Paperclip size={28} className="text-[var(--rammy-cyan-dark)]"/>
            </div>
            <div className="mt-4 text-2xl md:text-3xl font-display">Drop it — I&rsquo;ll take a look.</div>
            <div className="mt-2 text-sm text-[var(--rammy-text-sec)]">JPEG, PNG or WEBP · up to 5 MB · 3 images</div>
          </div>
        </div>
      )}
      {/* Soft warm ambient glows */}
      <div className="absolute inset-x-0 top-0 h-[520px] pointer-events-none -z-10">
        <div className="absolute left-1/4 top-24 w-[420px] h-[420px] rounded-full bg-[var(--rammy-cyan)]/15 blur-3xl"/>
        <div className="absolute right-1/4 top-40 w-[360px] h-[360px] rounded-full bg-[var(--rammy-accent)]/20 blur-3xl"/>
      </div>

      <div
        className={started
          ? "flex-1 min-h-0 w-full flex flex-col"
          : "max-w-3xl mx-auto px-6 text-center fade-up"}
      >
        {/* Once the conversation starts, the chat IS the page — a quiet canvas
            instead of a widget parked halfway down a marketing site. */}
        {started && (
          <div
            className="shrink-0 border-b border-[var(--rammy-border)] bg-[var(--rm-surface)]/80 backdrop-blur-sm"
            data-testid="hero-canvas-bar"
          >
            <div className="max-w-2xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--rm-ink-900)] p-1 shrink-0">
                <RobotFace
                  expression={busy ? "thinking" : previewPlaying ? "speaking" : expression}
                  size={32} round showHud={false}
                />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <div className="text-sm font-medium leading-none">Rammy</div>
                <div className="text-[11px] text-[var(--rammy-text-sec)] mt-0.5">
                  {busy ? "thinking…" : "here with you"}
                </div>
              </div>
              {remaining !== null && !locked && (
                <>
                  <span
                    className="text-[11px] text-[var(--rammy-text-muted)] font-mono hidden sm:inline"
                    data-testid="hero-remaining"
                  >
                    {remaining} free {remaining === 1 ? "reply" : "replies"} left
                  </span>
                  <span
                    className="text-[11px] text-[var(--rammy-text-muted)] font-mono sm:hidden"
                    data-testid="hero-remaining-compact"
                  >
                    {remaining} left
                  </span>
                </>
              )}
              <button
                type="button"
                onClick={() => { setMsgs([]); setLocked(false); }}
                className="w-9 h-9 rounded-full border border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] flex items-center justify-center text-[var(--rammy-text-sec)] shrink-0 transition-colors"
                aria-label="Back to the site"
                title="Back to the site"
                data-testid="hero-canvas-close"
              >
                <X size={16}/>
              </button>
            </div>
          </div>
        )}

        {!started && (
          <>
            {/* THE HERO — the Ring, live, with one line of real transcribed
                speech underneath. Then the input. Nothing else above the fold. */}
            <div className="mt-2 md:mt-6">
              <HeroRing />
            </div>
          </>
        )}

        {/* Chat thread — the canvas fills the screen; pre-chat it stays a card */}
        {started && (
          <div
            ref={threadRef}
            data-testid="hero-thread"
            className="flex-1 min-h-0 overflow-y-auto"
            style={{ overscrollBehavior: "contain" }}
          >
            <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 py-6 text-left space-y-3">
            {msgs.map((m, i) => {
              const prev = i > 0 ? msgs[i - 1] : null;
              const canVerify =
                m.role === "assistant" &&
                !m.streaming &&
                !m.verified &&
                !busy &&
                !locked &&
                prev?.role === "user" &&
                (prev.content || "").trim().length > 0 &&
                isUncertainReply(m.content);
              const originalQuestion = prev?.content?.trim() || "";
              return (
                <div key={i} className="flex justify-start">
                  <div className="w-full flex flex-col items-start gap-1.5">
                    <div className={`whitespace-pre-wrap ${m.role === "user" ? "rm-turn-user" : "rm-turn-rammy"}`}
                         data-testid={`hero-msg-${m.role}`}>
                      {m.images && m.images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2" data-testid="hero-msg-images">
                          {m.images.map((src, j) => (
                            <img key={j} src={src} alt="" className="max-h-40 rounded-xl border border-black/10 object-cover"/>
                          ))}
                        </div>
                      )}
                      {m.content
                        ? (m.streaming ? m.content : cleanReply(m.content))
                        : (m.streaming ? <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin"/> {m.verified ? "checking…" : "thinking…"}</span> : "")}
                      {m.streaming && m.content && <span className="inline-block w-1.5 h-3 bg-current align-middle ml-0.5 animate-pulse"/>}
                    </div>
                    {m.role === "assistant" && m.weather && (
                      <div className="pl-1">
                        <WeatherCard card={m.weather} testid={`hero-weather-${i}`}/>
                      </div>
                    )}
                    {/* Captured while there's no account to reach — show the
                        thing itself, and what unlocks it. */}
                    {m.role === "assistant" && Array.isArray(m.pendingActions) && m.pendingActions.length > 0 && (
                      <div className="pl-1 space-y-2" data-testid={`hero-pending-actions-${i}`}>
                        {m.pendingActions.map((a, k) => (
                          <PendingActionCard
                            key={a.id || k}
                            action={a}
                            testid={`hero-pending-action-${i}-${k}`}
                          />
                        ))}
                      </div>
                    )}
                    {showSources && m.verified && Array.isArray(m.sources) && m.sources.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pl-1" data-testid={`hero-sources-${i}`}>
                        <span className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold self-center">Sources</span>
                        {m.sources.map((s, k) => (
                          <a
                            key={k}
                            href={s.url || "#"}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="text-[11px] px-2 py-0.5 rounded-full bg-[var(--rm-surface)] border border-[var(--rammy-cyan)]/40 text-[var(--rammy-cyan-dark)] hover:bg-[var(--rammy-cyan)]/10 transition inline-flex items-center gap-1"
                            data-testid={`hero-source-link-${i}-${k}`}
                          >
                            <ExternalLink size={10}/>{s.title}
                          </a>
                        ))}
                      </div>
                    )}
                    {/* Provenance only — see SourceBadge for why there is no
                        confidence wording. */}
                    {m.role === "assistant" && !m.streaming && showConfidence &&
                     Array.isArray(m.sources) && m.sources.length > 0 && (
                      <div className="pl-1">
                        <SourceBadge credibility={m.credibility} sources={m.sources} query={originalQuestion}/>
                      </div>
                    )}
                    {m.role === "assistant" && !m.streaming && (m.content || "").trim() && (
                      <div className="pl-1 flex items-center gap-3 flex-wrap" data-testid={`hero-answer-tools-${i}`}>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(cleanReply(m.content));
                              toast.success("Copied the full reply");
                            } catch (_) {
                              toast.error("Couldn't copy — select and copy manually.");
                            }
                          }}
                          className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)] hover:underline"
                          data-testid={`hero-copy-reply-${i}`}
                          title="Copy the whole answer, including anything scrolled out of view"
                        >
                          <Copy size={11}/> Copy
                        </button>
                        {i === msgs.length - 1 && !busy && !locked && (
                          <>
                            <button
                              type="button"
                              onClick={() => regenerate("shorter")}
                              className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)] hover:underline"
                              data-testid={`hero-shorter-${i}`}
                              title="Same answer, fewer words"
                            >
                              <AlignLeft size={11}/> Shorter
                            </button>
                            <button
                              type="button"
                              onClick={() => regenerate(null)}
                              className="text-[11px] inline-flex items-center gap-1 text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)] hover:underline"
                              data-testid={`hero-regenerate-${i}`}
                              title="Try that again, differently"
                            >
                              <RefreshCw size={11}/> Try again
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    {canVerify && (
                      <button
                        type="button"
                        onClick={() => send(originalQuestion, { verify: true })}
                        className="text-[11px] pl-1 inline-flex items-center gap-1 text-[var(--rammy-cyan-dark)] hover:underline"
                        data-testid={`hero-verify-btn-${i}`}
                        title="Let me look this up for you"
                      >
                        <Search size={11}/> Let me check that for you
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}

        {/* Composer */}
        <div
          ref={composerRef}
          className={started
            ? "shrink-0 border-t border-[var(--rammy-border)] bg-[var(--rm-surface)]/80 backdrop-blur-sm px-4 sm:px-6 pt-3 pb-4 mx-auto w-full max-w-2xl"
            : "mx-auto max-w-2xl mt-10"}
        >
          {attachments.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2" data-testid="hero-attach-preview">
              {attachments.map((a, i) => (
                <div key={i} className="relative group" data-testid={`hero-attach-${i}`}>
                  <img src={a.preview} alt={a.name} className="h-16 w-16 object-cover rounded-xl border border-[var(--rammy-border)]"/>
                  <button
                    onClick={()=>removeAttachment(i)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[var(--rm-ink-900)] text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    aria-label="Remove"
                    data-testid={`hero-attach-remove-${i}`}
                  >×</button>
                </div>
              ))}
            </div>
          )}
          <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] rammy-shadow-lg p-2 flex items-end gap-2" data-testid="hero-composer">
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={(e)=>onPick(e.target.files)}
              data-testid="hero-attach-input"
            />
            <button
              onClick={()=>fileRef.current?.click()}
              disabled={locked || busy || attachments.length >= MAX_FILES}
              className="hidden sm:flex w-11 h-11 rounded-full border border-[var(--rammy-border)] hover:border-[var(--rammy-cyan)] items-center justify-center text-[var(--rammy-text-sec)] shrink-0 disabled:opacity-40 transition"
              aria-label="Attach image"
              data-testid="hero-attach"
              title="Attach an image"
            >
              <Paperclip size={16}/>
            </button>
            <button
              onClick={() => { voiceModeRef.current = true; setVoiceOpen(true); }}
              disabled={locked || busy}
              className="w-11 h-11 rounded-full border border-[var(--rm-brand-soft-brd)] bg-[var(--rm-brand-soft)] hover:border-[var(--rm-brand)] flex items-center justify-center text-[var(--rm-brand)] shrink-0 disabled:opacity-40 transition-colors"
              aria-label="Talk to Rammy"
              data-testid="hero-voice"
              title="Talk to Rammy — hands-free"
            >
              <Mic size={16}/>
            </button>
            <span className="hidden sm:inline-flex"><LocationButton testIdPrefix="hero-location" /></span>
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              onPaste={onPaste}
              placeholder={locked ? "Sign in to keep chatting with Rammy…" : (attachments.length ? "Ask Rammy about the image…" : "Message Rammy…")}
              disabled={locked}
              data-testid="hero-input"
              className="flex-1 min-w-0 resize-none bg-transparent outline-none px-3 sm:px-4 py-3 text-[16px] placeholder:text-[var(--rm-text-faint)] max-h-40"
              style={{ minHeight: 48 }}
            />
            <Button
              onClick={() => send()}
              disabled={busy || locked || (!input.trim() && attachments.length === 0)}
              className="btn-pill btn-primary h-11 px-4 shrink-0"
              data-testid="hero-send"
              aria-label="Send"
            >
              {busy ? <Loader2 size={16} className="animate-spin"/> : <ArrowRight size={18}/>}
            </Button>
          </div>

          {/* Answer shape. Companion is the default; Brief and Deep exist
              because a companion voice is the wrong shape for a briefing. */}
          <div
            className="mt-3 flex items-center justify-center gap-1"
            data-testid="hero-mode-switch"
          >
            {[
              { k: "companion", l: "Companion", t: "Warm, short, conversational" },
              { k: "brief", l: "Brief", t: "The answer and nothing else" },
              { k: "deep", l: "Deep", t: "A proper briefing — options, trade-offs, assumptions, what I'm unsure about" },
            ].map(({ k, l, t }) => (
              <button
                key={k}
                type="button"
                onClick={() => setAnswerMode(k)}
                title={t}
                aria-pressed={answerMode === k}
                data-testid={`hero-mode-${k}`}
                className={answerMode === k
                  ? "text-[11px] px-3 py-1 rounded-full bg-[var(--rammy-cyan)]/18 border border-[var(--rammy-cyan)]/50 text-[var(--rammy-cyan-dark)] font-medium transition-colors"
                  : "text-[11px] px-3 py-1 rounded-full border border-transparent text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] hover:border-[var(--rammy-border)] transition-colors"}
              >
                {l}
              </button>
            ))}
          </div>

          {!locked && !started && (
            <div className="mt-5 flex flex-wrap justify-center gap-2" data-testid="hero-suggestions">
              {SUGGESTIONS.map(s => (
                <button
                  key={s.label}
                  onClick={() => send(s.text)}
                  disabled={busy}
                  data-testid={`hero-suggest-${s.label.replace(/[?!.,]/g, '').replace(/\s+/g,'-').toLowerCase()}`}
                  className={
                    s.magic
                      ? "text-sm px-4 py-2 rounded-full bg-[var(--rammy-cyan)]/12 border border-[var(--rammy-cyan)]/40 text-[var(--rammy-cyan-dark)] hover:bg-[var(--rammy-cyan)]/20 hover:border-[var(--rammy-cyan)] transition font-medium"
                      : "text-sm px-4 py-2 rounded-full border border-[var(--rammy-border)] bg-[var(--rm-surface)]/70 hover:bg-[var(--rm-surface)] hover:border-[var(--rammy-cyan)] transition"
                  }
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {/* One quiet line, once — not a conversion module under every answer.
              The audit: repeated save-prompts train people to skim past
              Rammy's own words. */}
          {started && !locked && msgs.some(m => m.role === "assistant" && !m.streaming && m.content) && (
            <div
              className="mt-3 flex items-center justify-center gap-2 text-[11px] text-[var(--rammy-text-muted)] flex-wrap"
              data-testid="hero-continue-nudge"
            >
              <span>This chat lives in this tab only.</span>
              <Link
                to="/signup"
                data-testid="hero-continue-signup"
                className="text-[var(--rammy-cyan-dark)] hover:underline font-medium"
              >
                Keep it — free account
              </Link>
            </div>
          )}

          <div className="mt-4 flex items-center justify-center gap-4 text-xs text-[var(--rammy-text-muted)] flex-wrap">
            {!started && (
              <>
                <span className="inline-flex items-center gap-1" data-testid="hero-free-note">
                  <Sparkles size={12}/>
                  {remaining === null
                    ? "Free to try — no signup"
                    : `Free to try — ${remaining} message${remaining === 1 ? "" : "s"} as a guest, no signup`}
                </span>
                <span className="hidden sm:inline">·</span>
                <span className="inline-flex items-center gap-1"><ShieldCheck size={12}/> Private, no ads, no tracking</span>
              </>
            )}
          </div>

          {locked && (
            <div
              className="mt-3 rounded-2xl bg-[var(--rm-ink-900)] text-white px-4 py-3 flex items-center gap-3 flex-wrap justify-between fade-up"
              data-testid="hero-locked"
            >
              <div className="min-w-0 text-left">
                <div className="text-sm font-medium">That's the guest preview — the thread carries on in a free account.</div>
                <div className="text-[11px] text-white/55 mt-0.5">
                  Everything above stays, and chat stops being counted. No card, no tracking.
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Link to="/signup">
                  <Button className="btn-pill btn-primary h-9 px-4 text-xs" data-testid="hero-locked-signup">
                    Keep this chat — free
                  </Button>
                </Link>
                <Link to="/login">
                  <Button className="btn-pill h-9 px-3 bg-white/10 text-white hover:bg-white/20 text-xs" data-testid="hero-locked-signin">
                    Sign in
                  </Button>
                </Link>
              </div>
            </div>
          )}

        </div>
      </div>
      {voiceOpen && (
        <VoiceMode
          voice="hale"
          anonSessionId={getAnonSessionId()}
          focusMode={voiceFocusMode}
          onFocusModeChange={setVoiceFocusMode}
          onExchange={({ user: uTxt, assistant }) => {
            // The streaming pipeline does STT, the reply and the speech
            // server-side; we just mirror the exchange into the visible thread.
            setMsgs((m) => [
              ...m,
              ...(uTxt ? [{ role: "user", content: uTxt }] : []),
              ...(assistant ? [{ role: "assistant", content: cleanReply(assistant) }] : []),
            ]);
          }}
          onClose={() => { voiceModeRef.current = false; setVoiceOpen(false); }}
        />
      )}
    </section>
  );
}
